import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  agentConversationDetailSchema,
  agentConversationFileListSchema,
  agentConversationSchema,
  agentLibraryFileListSchema,
  /* C48-1 · the server's own predicate for *"is this a table"*, so a `.pdf` never leaves the tab. */
  agentImageTypeOf,
  tableFormatOf,
  type AgentConversation,
  type AgentConversationFile,
  type AgentFrame,
  type AgentLibraryFile,
} from '@forecastify/shared';
import { Toaster } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { useSession } from '../../session';
import {
  AgentRefusal,
  agentAccept,
  agentDecline,
  agentRevise,
  agentTurn,
  refusalFrom,
} from './agent-turn';
import { AgentScreen, type AgentPhase, type ThreadStep, type ThreadTurn } from './agent-screen';
import { takeAgentHandoff } from './agent-handoff';
import type { ProposalState } from './agent-proposal-card';
/* ⚠️ C48 · the one export path (T-10). The Agent's offer resolves a filter and this runs it, from
 * the dialog the rest of the product opens. */
import { useExport } from '../exports/use-export';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27.1 · the Agent route: one conversation, one turn at a time.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The route owns the stream; the screen owns the mockup's geometry.** Every number on that
 * screen is measured out of `markup.html`, and mixing a reducer over SSE frames into the same file
 * would make the geometry the part nobody dares touch.
 *
 * ⚠️⚠️ **THE CONVERSATION IS CREATED LAZILY, ON THE FIRST MESSAGE.** Creating one on mount would
 * write a row every time somebody clicks *Agent* in the sidebar and changes their mind — and since
 * `updated_at` orders the history menu C27.4 will build, the list would fill with empty threads
 * ahead of the real ones. A conversation exists because somebody said something.
 *
 * ⚠️ **One turn at a time, and no queue.** `phase !== 'idle'` disables sending. A queue would let
 * somebody stack three questions whose answers arrive interleaved with each other's context, and the
 * cap on this conversation would be reached by a thread nobody could follow.
 */

/**
 * ⚠️⚠️ **THE REFUSAL CODES, MAPPED TO CATALOGUE KEYS — RULE 5 IN ONE OBJECT.** *"Clients branch on
 * `code`, never on `message`."* The server's message is English and written for whoever is on call;
 * this is what a person reads, in HU or EN.
 *
 * ⚠️ **`agent_not_configured` and `feature_not_enabled` are deliberately ABSENT from this map.**
 * They are not failures of a turn — they are the feature being off, which is a different shape on
 * screen: the composer is disabled and the notice explains why, rather than an alert about something
 * that just went wrong. `blockedBy` below handles them.
 */
const ERROR_KEY: Readonly<Record<string, MessageKey>> = {
  rate_limited: 'agent.error.rate_limited',
  turn_too_long: 'agent.error.turn_too_long',
  upstream_unavailable: 'agent.error.upstream_unavailable',
  conversation_not_found: 'agent.error.conversation_not_found',
  conversation_not_renamable: 'agent.error.conversation_not_renamable',
  proposal_not_found: 'agent.error.proposal_not_found',
  proposal_expired: 'agent.error.proposal_expired',
  proposal_already_used: 'agent.error.proposal_already_used',
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48-1 · THE FOUR AN UPLOAD CAN ANSWER WITH — AND C43 CLAIMED THESE  │
   * │ WERE ALREADY HERE.                                                       │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `attach` below says *"each already has a catalogue string from C22 — so this reuses
   * `setFailure`"*, and the strings did exist; the four rows that reach them did not. Every upload
   * refusal fell through `errorKey`'s default to *"Az Agent nem tudott válaszolni. Próbáld újra."* —
   * a sentence about the model, under a screen where nothing had been asked of the model.
   *
   * ⚠️ **The drag-and-drop is what turned that from untidy into wrong.** A picker filtered by
   * `accept` made `unsupported_file_type` nearly unreachable; `accept` does nothing at all for a
   * drop, so *"I dropped a PDF"* is now the ordinary case, and it has to say so.
   *
   * ⚠️ **Three are C22's own strings**, reused as `contract-detail.tsx` reuses them: a file being
   * too large or refused by the scanner is the same fact wherever it is uploaded. **The fourth is
   * not**, because *"the upload did not go through"* is not what happened — the file arrived and was
   * declined for a reason this screen can state precisely.
   */
  file_too_large: 'task.attachment.tooLarge',
  file_infected: 'task.attachment.infected',
  scanner_unavailable: 'task.attachment.scannerDown',
  unsupported_file_type: 'agent.files.notTabular',
};

/**
 * What a refused confirmation leaves on the card.
 *
 * ⚠️⚠️ **A REFUSED ACCEPT MUST SETTLE THE CARD, NOT LEAVE TWO LIVE BUTTONS UNDER AN ERROR LINE.** All
 * three of these mean *this card can never be acted on again* — expired, already answered, or gone —
 * so the buttons go and the card says which. Leaving them would invite a second press that produces
 * the same refusal.
 */
const REFUSED_STATE: Readonly<Record<string, ProposalState>> = {
  proposal_expired: 'expired',
  proposal_already_used: 'used',
  proposal_not_found: 'used',
};

/** ⚠️ An unmapped code still says something — `screen-state.tsx`'s rule, and it applies here too. */
function errorKey(code: string): MessageKey {
  return ERROR_KEY[code] ?? 'agent.error.unknown';
}

function blockedBy(code: string): 'notConnected' | 'notInPlan' | null {
  if (code === 'agent_not_configured') return 'notConnected';
  if (code === 'feature_not_enabled') return 'notInPlan';
  return null;
}

export function AgentRoute() {
  const session = useSession();
  const t = useT();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE ADDRESS DECIDES WHICH THREAD IS OPEN. Nothing else does.        │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `undefined` on `/agent`, which is a NEW conversation — created lazily on the first message, so
   * the empty address is a real state rather than a placeholder for one.
   *
   * ⚠️⚠️ **ONE DIRECTION, AND IT IS WHAT KEEPS THE TWO FROM DISAGREEING.** Every gesture that changes
   * which thread is open — a row in the history menu, *new*, *delete*, and the first message of a
   * fresh thread — **navigates**, and the effect below is the only thing that loads. The obvious
   * alternative (load here, and also push the address) is two sources of truth for one fact, and the
   * bug it produces is the address naming one conversation while the screen shows another.
   */
  const { conversationId } = useParams<{ conversationId: string }>();

  const [conversation, setConversation] = useState<AgentConversation | null>(null);
  /**
   * ⚠️ **The history menu's contents, in the SERVER's order** (`updated_at desc`). Not re-sorted here:
   * a thread replied to today is today's thread, and a second ordering would be a second answer to
   * what *recent* means.
   */
  const [conversations, setConversations] = useState<readonly AgentConversation[]>([]);
  /** Until the list's first read settles, so the history menu does not say there are none. */
  const [conversationsLoading, setConversationsLoading] = useState(true);
  /** C43 · an upload is in flight, so the paperclip says so rather than looking idle. */
  const [attaching, setAttaching] = useState(false);
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C47 · THIS THREAD'S FILES, IN ONE PLACE, BECAUSE THREE SURFACES     │
   * │ RENDER THEM.                                                             │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * C43 held no file state at all: `Attachments` fetched its own list, and the route nudged it by
   * bumping a counter used as a React `key` — *"the smallest honest way to say ask again without
   * reaching into somebody else's component"*, which it was, for one surface. There are three now:
   * a chip above each message, the strip above the composer, and the panel's *this conversation*
   * half. A remount-triggering counter cannot serve them, and three components fetching the same
   * five rows would be three chances to disagree about which of them has been sent.
   *
   * ⚠️ **Each row carries `messageId`, and the null is the whole mechanism** — pending above the
   * composer, bound above its message. `0095` argues why that is a stored fact.
   */
  const [files, setFiles] = useState<readonly AgentConversationFile[]>([]);
  /**
   * Every file this member has ever handed the Agent, for the browse panel.
   *
   * ⚠️ **Loaded when the panel opens, not on mount.** It is a request nobody needs until they reach
   * for it, and on a screen whose first paint is a composer somebody is about to type in.
   */
  const [library, setLibrary] = useState<readonly AgentLibraryFile[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [turns, setTurns] = useState<readonly ThreadTurn[]>([]);
  const [phase, setPhase] = useState<AgentPhase>('idle');
  const [blocked, setBlocked] = useState<'notConnected' | 'notInPlan' | null>(null);
  const [failure, setFailure] = useState<MessageKey | null>(null);

  /**
   * ⚠️ **The in-flight turn's abort handle, in a ref.** State would re-render the whole thread on
   * every send; and what this is for — cancelling — is not something the screen renders.
   */
  const abort = useRef<AbortController | null>(null);

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE LIST REQUEST IS HOW THE SCREEN LEARNS THE AGENT IS OFF.      │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * There is no `GET /agent/status`, deliberately: the screen loads its conversations on mount
   * anyway, and the refusal it gets back — `agent_not_configured` or `feature_not_enabled` — is the
   * signal. One endpoint fewer to keep true, and rule 5 already says clients branch on `code`.
   *
   * ⚠️ **A failure here does NOT render an error screen.** Both codes mean *the feature is off*,
   * which is a disabled composer with an explanation — C17's shell exactly. `ScreenState` would put
   * a retry button in front of a deployment decision.
   */
  useEffect(() => {
    /* ⚠️ **No `let live = true` guard, matching every other route in this app.** React 18 makes a
     * `setState` after unmount a silent no-op, so the flag buys nothing — and a `let` assigned only
     * inside a cleanup closure is a value TypeScript can prove never changes, which the lint rule
     * correctly reports as an always-falsy check. The only write below is idempotent besides. */
    void (async () => {
      const response = await session.request('/agent/conversations');

      if (!response.ok) {
        const { code } = await refusalFrom(response);
        const block = blockedBy(code);
        /* ⚠️ An unexpected code leaves the composer usable rather than dead: the send will refuse
         * with its own reason, which is more informative than a screen that pre-emptively gives up
         * on a 500 that may not recur. */
        if (block !== null) setBlocked(block);
        return;
      }

      /* ⚠️ Parsed with the shared schema, never cast — `hard-won.md` C19-1: a shape nobody checks is
       * one that silently changes meaning the first time the contract moves. */
      const body: unknown = await response.json().catch(() => null);
      const parsed = agentConversationSchema.array().safeParse(body);
      if (!parsed.success) return;
      setConversations(parsed.data);
      setBlocked(null);
    })().finally(() => {
      setConversationsLoading(false);
    });
  }, [session]);

  /** Stop the turn. See `agent-screen.tsx` — this cancels the request, which stops the model. */
  const stop = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setPhase('idle');
  }, []);

  /* ⚠️ On unmount too: navigating away from the Agent must not leave a turn generating. */
  useEffect(
    () => () => {
      abort.current?.abort();
    },
    [],
  );

  /**
   * Mark a question as one that got no answer.
   *
   * ⚠️ **Set from the failure paths rather than derived from "has no reply after it"**, because
   * mid-stream a question legitimately has nothing after it for a second or two — a derived rule
   * would flash red on every send. `openConversation` derives it for the history, where the thread
   * is finished and the derivation is sound.
   */
  const markFailed = useCallback((id: string) => {
    setTurns((current) =>
      current.map((turn) => (turn.id === id ? { ...turn, failed: true } : turn)),
    );
  }, []);

  /**
   * 29 Oct 2026 · a thread's title changed — the provisional one, the model's summary, or a rename.
   *
   * ⚠️ **Both copies**: the menu's list and the open thread. They are separate state for C27.4's
   * reasons, and a title changed in one would leave the other naming the thread differently.
   */
  const retitle = useCallback((id: string, title: string | null) => {
    setConversations((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, title } : entry)),
    );
    setConversation((current) => (current?.id === id ? { ...current, title } : current));
  }, []);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE REDUCER, TWO STREAMS. C27.3 made this an extraction rather than   │
   * │ a copy.                                                                   │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * A question and a confirmed write both answer with the agent's frame vocabulary — the second is
   * *"the write happened, now say what happened"* — and a second copy of this loop would be a second
   * place for the anti-fabrication rules to drift: that a card is only ever rendered from a `data`
   * frame, that a failed turn drops a partial sentence, and that nothing here derives a row.
   *
   * ⚠️ **The draft turn is created on `message_start`**, not before, so a stream that refuses before
   * the model speaks leaves no empty bubble behind.
   */
  const consume = useCallback(
    async (
      stream: AsyncGenerator<AgentFrame, void, undefined>,
      draftId: string,
      /**
       * The question this stream is answering, to be marked in red if it never gets an answer — or
       * `null` when the stream is not answering a question at all.
       *
       * ⚠️⚠️ **`null` FOR A CONFIRMATION, AND THAT IS THE WHOLE REASON THIS IS A PARAMETER RATHER
       * THAN *"the last user turn"*.** An accept or a decline streams through this same reducer; a
       * failure there is a failed *write*, and reddening whatever question happens to be last would
       * blame a sentence that was answered perfectly well several turns ago.
       */
      questionId: string | null,
      /** 29 Oct 2026 · the thread this stream belongs to, for its `conversation_title` frames. */
      conversationId: string | null,
    ): Promise<void> => {
      let started = false;
      /**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ 29 Oct 2026 · THE TURN IS OVER AT `message_stop`, AND THE STREAM │
       * │ MAY OUTLIVE IT.                                                       │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * The thread's summarised title follows `message_stop` on the same stream (`0246`), so for a
       * moment the composer is free while this loop still reads. Two consequences, both guarded by
       * this flag: a stream that fails in that moment is NOT a failed turn — the answer is whole —
       * and the loop's closing `setPhase('idle')` must not fire, because by then somebody may have
       * asked the next question and that turn's phase is not this stream's to reset.
       */
      let finished = false;

      try {
        for await (const frame of stream) {
          /* ⚠️ Before the `started` guard: the provisional title comes right behind `message_start`,
           and the summary after `message_stop` — neither touches a bubble. */
          if (frame.type === 'conversation_title') {
            if (conversationId !== null) retitle(conversationId, frame.title);
            continue;
          }
          if (frame.type === 'message_start') {
            started = true;
            setTurns((current) => [
              ...current,
              {
                id: draftId,
                role: 'assistant',
                text: '',
                thinking: null,
                steps: [],
                blocks: [],
                /* ⚠️ C48 · appended as `export_offer` frames arrive, like `blocks`. */
                offers: [],
                proposal: null,
                /* ⚠️ An assistant turn is never `failed`: a turn that failed leaves no assistant
                 * bubble at all — see the `error` branch, which drops the empty draft. What carries
                 * the mark is the QUESTION, because that is what stays on screen. */
                failed: false,
                /* 29 Oct 2026 · set by a `compacted` frame, if this turn summarises the thread. */
                compacted: false,
              },
            ]);
            continue;
          }

          /* ⚠️ Defensive: a stream that sends a delta before `message_start` would otherwise append to
           * the user's own bubble. The server always sends it first; this is the guard that makes that
           * a property rather than an assumption. */
          if (!started) continue;

          if (frame.type === 'thinking_start') {
            setPhase('thinking');
            continue;
          }
          if (frame.type === 'thinking_delta') {
            setPhase('thinking');
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId
                  ? { ...turn, thinking: (turn.thinking ?? '') + frame.text }
                  : turn,
              ),
            );
            continue;
          }
          /**
           * ┌───────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ C48 · AN EXPORT OFFER JOINS THE DRAFT TURN. R-34.                │
           * └───────────────────────────────────────────────────────────────────────┘
           *
           * ⚠️ **Appended rather than replaced**, because a turn may hold more than one: the model can
           * set up an hours export and a tasks export in one answer, and each is its own control with
           * its own filter. A field would have silently kept the last one.
           */
          if (frame.type === 'export_offer') {
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId ? { ...turn, offers: [...turn.offers, frame.offer] } : turn,
              ),
            );
            continue;
          }

          if (frame.type === 'text_delta') {
            setPhase('answering');
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId ? { ...turn, text: turn.text + frame.text } : turn,
              ),
            );
            continue;
          }
          /**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ A TOOL ROW APPEARS WHEN THE MODEL NAMES THE TOOL, NOT    │
           * │ WHEN THE QUERY RUNS.                                          │
           * └───────────────────────────────────────────────────────────────┘
           *
           * The server emits `tool_start` from the content-block boundary, while the arguments are
           * still streaming — which is the second or two a person would otherwise spend watching
           * nothing happen. The query itself is milliseconds.
           */
          /* 29 Oct 2026 · the thread before this answer was summarised; the bubble says so. */
          if (frame.type === 'compacted') {
            setTurns((current) =>
              current.map((turn) => (turn.id === draftId ? { ...turn, compacted: true } : turn)),
            );
            continue;
          }

          if (frame.type === 'tool_start') {
            const step: ThreadStep = {
              id: frame.toolCallId,
              name: frame.name,
              state: 'running',
              count: null,
              errorCode: null,
            };
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId ? { ...turn, steps: [...turn.steps, step] } : turn,
              ),
            );
            continue;
          }

          if (frame.type === 'tool_end') {
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId
                  ? {
                      ...turn,
                      steps: turn.steps.map((step) =>
                        step.id === frame.toolCallId
                          ? {
                              ...step,
                              state: frame.ok ? ('done' as const) : ('failed' as const),
                              count: frame.count,
                              errorCode: frame.errorCode,
                            }
                          : step,
                      ),
                    }
                  : turn,
              ),
            );
            continue;
          }

          /**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE ONLY WAY A TABLE GETS ONTO THIS SCREEN.               │
           * └───────────────────────────────────────────────────────────────┘
           *
           * The block is appended exactly as it arrived — already validated against
           * `agentDataBlockSchema` by `agentTurn`, and already permission- and field-trimmed by the
           * service the tool called. Nothing here derives a row, sums a column or fills a gap: a
           * client that computed anything on this path would be the model authoring rows by proxy,
           * which is the fabrication §4 exists to prevent.
           */
          if (frame.type === 'data') {
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId ? { ...turn, blocks: [...turn.blocks, frame.block] } : turn,
              ),
            );
            continue;
          }

          /**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ A WRITE, OFFERED. The turn ends here and nothing has     │
           * │ changed in the workspace. R-25.                               │
           * └───────────────────────────────────────────────────────────────┘
           *
           * ⚠️ **The summary is rendered as it arrived** — see `agent-proposal-card.tsx`: it is the one
           * sentence the API composes, because the words somebody approves and the row that gets
           * written must come from the same call. Nothing here reformats a figure or a date.
           */
          if (frame.type === 'proposal') {
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId
                  ? { ...turn, proposal: { ...frame.proposal, state: 'pending' as const } }
                  : turn,
              ),
            );
            continue;
          }

          if (frame.type === 'message_stop') {
            setTurns((current) =>
              current.map((turn) =>
                turn.id === draftId ? { ...turn, id: frame.messageId } : turn,
              ),
            );
            setPhase('idle');
            finished = true;
            continue;
          }
          if (frame.type === 'error') {
            /**
             * ⚠️⚠️ **THE PARTIAL REPLY IS DROPPED, AND THAT IS THE ANTI-FABRICATION RULE.** A turn
             * that failed halfway has written a sentence that stops mid-thought — which is
             * indistinguishable from an answer, and C17 built an empty screen precisely to keep that
             * out of this product. So the bubble goes and the notice says what happened.
             *
             * ⚠️ Only when it is genuinely partial: a failure after a complete reply (a storage
             * error, say) keeps the text, because there the answer *was* the answer.
             *
             * ⚠️ **A draft with a CARD in it is not empty, and that is a C27.2 addition.** A turn that
             * read two tasks and then lost the socket has already put a real table on screen — rows a
             * service returned, stamped with the moment they were read. Dropping the bubble would take
             * the evidence with it. What is dropped is what was always dropped: an empty bubble.
             */
            setTurns((current) =>
              current.filter(
                (turn) =>
                  !(
                    turn.id === draftId &&
                    turn.text.trim() === '' &&
                    turn.blocks.length === 0 &&
                    turn.proposal === null
                  ),
              ),
            );
            if (questionId !== null) markFailed(questionId);
            setFailure(errorKey(frame.code));
            setPhase('idle');
          }
        }
      } catch (error) {
        /* ⚠️ The answer arrived whole; what was lost is at most a title. Not a failure. */
        if (finished) return;
        throw error;
      }

      /* ⚠️ A stream that ended without `message_stop` or `error` — the socket died. The phase must
       * still return to idle or the composer stays disabled for ever. */
      if (!finished) setPhase('idle');
    },
    [markFailed, retitle],
  );

  /** Move one card into a settled state, wherever in the thread it is. */
  const markProposal = useCallback((id: string, state: ProposalState) => {
    setTurns((current) =>
      current.map((turn) =>
        turn.proposal?.id === id ? { ...turn, proposal: { ...turn.proposal, state } } : turn,
      ),
    );
  }, []);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C43 · STARTING THE THREAD, EXTRACTED — BECAUSE TWO THINGS NOW NEED  │
   * │ A CONVERSATION AND ONLY ONE OF THEM IS A QUESTION.                       │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * A file attaches to a conversation, so somebody who opens `/agent` and reaches for the paperclip
   * before typing needs the thread created first. This was inline in `send`; a second copy would
   * have been a second place holding the four steps that have to happen in this order — and the
   * ordering is the part with the comments on it.
   */
  const create = useCallback(async (): Promise<AgentConversation> => {
    const created = await session.request('/agent/conversations', { method: 'POST' });
    if (!created.ok) throw await refusalFrom(created);
    const parsed = agentConversationSchema.safeParse(
      (await created.json().catch(() => null)) as unknown,
    );
    if (!parsed.success) throw new AgentRefusal('upstream_unavailable', created.status);

    setConversation(parsed.data);
    /* ⚠️ **Into the menu immediately**, so the thread somebody is typing in is the one the history
     * shows as open. The list is otherwise only read on mount. */
    setConversations((current) => [parsed.data, ...current]);
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE ADDRESS CATCHES UP HERE, AND `replace` IS THE PART WORTH     │
     * │ ARGUING.                                                              │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * A conversation exists from its first message — or, since C43, from its first attachment — so
     * this is the moment the thread acquires an address, and from here a reload keeps it.
     *
     * ⚠️ **`replace: true`, because `/agent` and `/agent/<this thread>` are the same screen at two
     * moments of one action.** A push would put the empty composer in the history, so Back from a
     * thread you just started lands on a blank version of itself rather than wherever you came from.
     *
     * ⚠️ **AFTER `setConversation`**, which is what lets the sync effect recognise this id as
     * already open instead of aborting the stream about to be consumed by the caller.
     */
    go(`/agent/${parsed.data.id}`, { replace: true });
    return parsed.data;
  }, [go, session]);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C43 · THE UPLOAD, AND IT GOES THROUGH C22's ORDINARY ENDPOINT.      │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `POST /files?target=agent_conversation&targetId=…` — the same route a comment's attachment uses,
   * with the same multipart limit, the same ClamAV quarantine and the same content-addressed store.
   * There is no agent-specific upload path, which is why adding this target was a migration and a
   * gate rather than a controller.
   *
   * ⚠️ **The file is uploaded when it is picked**, not staged until send (`agent-screen.tsx` argues
   * the difference from `comment-composer.tsx`): the thread exists, the file belongs to the thread,
   * and the chip appearing straight away is what tells somebody the Agent can see it.
   *
   * ⚠️ **A refusal lands in the same notice as a refused turn.** `unsupported_file_type`,
   * `file_too_large`, `file_infected` and `scanner_unavailable` are the four the server can answer
   * with here, and each already has a catalogue string from C22 — so this reuses `setFailure` rather
   * than growing a second error surface beside it.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C47 · ONE READ FOR THE THREAD'S FILES, CALLED FROM FIVE PLACES.     │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Opening a thread, an upload landing, a re-attach from the panel, a removal, and the first message
   * of a brand-new conversation all change the same list. Each of them calls this.
   *
   * ⚠️ **A failure leaves the list ALONE rather than emptying it.** A dropped request that cleared
   * the chips would report the files as gone — and on the pending strip that reads as an upload
   * having been lost, which is the one thing this screen must not say when it is not true.
   */
  const loadFiles = useCallback(
    async (id: string): Promise<void> => {
      const response = await session.request(`/agent/conversations/${id}/files`).catch(() => null);
      if (response?.ok !== true) return;
      const parsed = agentConversationFileListSchema.safeParse(
        (await response.json().catch(() => null)) as unknown,
      );
      if (!parsed.success) return;

      /**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ A LOCAL BINDING SURVIVES A RE-READ, AND WITHOUT THIS ATTACHING  │
       * │ A SECOND FILE MID-TURN WIPED THE FIRST ONE'S CHIP.                   │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * `send` binds the pending files to the OPTIMISTIC turn's id, because the stored message's uuid
       * is not something this client has seen — the turn on screen is still `user:…`. The server
       * binds them to the real one. So a plain `setFiles` of what came back re-points every chip at a
       * message id no turn has, and the chips vanish from a question that is still on screen.
       *
       * ⚠️ **Reachable, and not a theoretical race**: the paperclip stays enabled while a turn is
       * streaming (attaching is not sending), so *"ask, then attach the next month's file while it
       * answers"* is an ordinary thing to do — and every one of the four callers of this function
       * re-reads.
       *
       * ⚠️ **Keyed on the link id, which is why a stale entry cannot leak into another thread**: ids
       * are unique per link, so a file from the conversation just left matches nothing in the one just
       * opened. ⚠️ And on the `user:` prefix, which `send` documents as the property that keeps an
       * optimistic id from ever colliding with a stored uuid.
       */
      setFiles((current) => {
        const local = new Map(
          current
            .filter((file) => file.messageId?.startsWith('user:') === true)
            .map((file) => [file.id, file.messageId]),
        );
        return parsed.data.data.map((file) => {
          const held = local.get(file.id);
          return held === undefined ? file : { ...file, messageId: held };
        });
      });
    },
    [session],
  );

  /**
   * The library behind the panel.
   *
   * ⚠️ **Re-read every time the panel opens**, not cached for the session: a file attached in another
   * tab, or one uploaded ten seconds ago in this thread, belongs in it. It is one small request
   * against a member's own rows.
   */
  const loadLibrary = useCallback(async (): Promise<void> => {
    setLibraryLoading(true);
    try {
      const response = await session.request('/agent/files').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = agentLibraryFileListSchema.safeParse(
        (await response.json().catch(() => null)) as unknown,
      );
      if (!parsed.success) return;
      setLibrary(parsed.data.data);
    } finally {
      setLibraryLoading(false);
    }
  }, [session]);

  /**
   * Open or close the panel.
   *
   * ⚠️ **The fetch hangs off the OPEN, not off a mount effect**, so closing and reopening asks again
   * and nothing asks while it is shut.
   */
  const showLibrary = useCallback(
    (open: boolean) => {
      setLibraryOpen(open);
      if (open) void loadLibrary();
    },
    [loadLibrary],
  );

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C47 · SEND A FILE THE MEMBER ALREADY HAS TO THIS THREAD.            │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **`create()` here too**, for C43's reason: somebody can open the panel on a thread that does
   * not exist yet and pick a file out of last month's conversation, and the file has to have
   * somewhere to land.
   *
   * ⚠️ **The panel stays open.** The row it was pressed on loses its button — the chip has appeared
   * in the strip behind the panel and in the *this conversation* section above — and somebody
   * attaching two files should not have to reopen the panel between them.
   */
  const attachExisting = useCallback(
    (fileId: string) => {
      void (async () => {
        setFailure(null);
        setAttaching(true);
        try {
          const target = conversation ?? (await create());
          const response = await session.request(`/agent/conversations/${target.id}/files`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ fileId }),
          });
          if (!response.ok) throw await refusalFrom(response);
          await loadFiles(target.id);
        } catch (error) {
          if (error instanceof AgentRefusal) {
            const block = blockedBy(error.code);
            if (block !== null) {
              setBlocked(block);
              return;
            }
            setFailure(errorKey(error.code));
            return;
          }
          setFailure(errorKey('upstream_unavailable'));
        } finally {
          setAttaching(false);
        }
      })();
    },
    [conversation, create, loadFiles, session],
  );

  /**
   * Take a file back off the thread before it has been asked about.
   *
   * ⚠️⚠️ **ONLY OFFERED WHILE IT IS PENDING, AND THE REASON IS THE TRANSCRIPT.** `DELETE /files/:id`
   * removes the link whatever state it is in — but a file already sent is part of a question
   * somebody asked and the model answered, and removing it would leave a message whose chips no
   * longer match the reply underneath it. The strip is where the × lives; the chips above a message
   * have none. `debt.md` carries the thought that the server should refuse a bound link too, which
   * is a rule and therefore a decision.
   */
  const removeFile = useCallback(
    (id: string) => {
      void (async () => {
        if (conversation === null) return;
        await session.request(`/files/${id}`, { method: 'DELETE' }).catch(() => null);
        await loadFiles(conversation.id);
      })();
    },
    [conversation, loadFiles, session],
  );

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48-1 · A LIST, UPLOADED ONE AT A TIME, AND ONE REFUSAL DOES NOT   │
   * │ CANCEL THE REST.                                                         │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The drop path can hand this three files (`file-drop.ts`), the endpoint takes one, so this is a
   * loop. Three things about the loop are decisions rather than shape:
   *
   * ⚠️⚠️ **SEQUENTIAL, NOT `Promise.all`.** Each upload is scanned by ClamAV before it exists, and
   * three 15 MB files scanned in parallel is three times the scanner's memory for no gain a person
   * can perceive. It also keeps `loadFiles` from racing itself — the chips appear one at a time,
   * which is the honest picture of what is happening.
   *
   * ⚠️⚠️ **A REFUSED FILE DOES NOT ABANDON THE ONES BEHIND IT, WHICH IS THE OPPOSITE OF WHAT A
   * `try` AROUND THE LOOP WOULD DO.** Drop three months of exports where the middle one is 20 MB and
   * the honest outcome is *two files attached and a sentence about the third* — not one file
   * attached, one refused, and one that silently never went. So each file has its own `catch`, the
   * FIRST refusal is what the notice says, and the rest of the batch still goes.
   *
   * ⚠️⚠️ **AND THE UNACCEPTABLE ONES NEVER LEAVE THE BROWSER.** `tableFormatOf` is the server's own
   * predicate (`@forecastify/shared`), so a `.pdf` is declined here rather than uploaded, scanned
   * and refused — which is a round trip and a virus scan spent on an answer that was already known.
   * The server still refuses it too, because a picker's filter is UX and the check is the server's
   * (rule 6): this only decides which sentence somebody reads first.
   */
  /**
   * ⚠️ 16 Oct 2026 · **AWAITABLE, AND IT ANSWERS WITH THE THREAD.** Kezdőlap hands a question over
   * with its files (`agent-handoff.ts`), and the files must be on the thread BEFORE the question is
   * sent — so the hand-off awaits this and passes the thread to `send`, which would otherwise read
   * the not-yet-updated `conversation` and start a second one. `null` when nothing was attached.
   */
  const upload = useCallback(
    async (chosen: readonly File[]): Promise<AgentConversation | null> => {
      if (chosen.length === 0) return null;
      setFailure(null);

      /**
       * ⚠️⚠️ **OR A PICTURE — and this ONE LINE was the whole of the bug.** Item 14 built the
       * image path end to end: the paste handler, the `accept` list, `file.service.ts`'s
       * `agentImageTypeOf` branch and `agent.service.ts` putting the image in the turn. Every
       * piece worked. This filter still asked `tableFormatOf` alone, so a screenshot was dropped
       * in the browser before any of it ran — and the sentence it produced said the Agent reads
       * tables, which by then was no longer true.
       *
       * ⚠️ **`file.type` first, the name second** — `agentImageTypeOf` takes both because a
       * pasted screenshot arrives as `image/png` with a generated name, and a file picked off
       * disk may arrive with an empty type and a real extension.
       */
      const sendable = chosen.filter(
        (file) =>
          tableFormatOf(file.name) !== null || agentImageTypeOf(file.type, file.name) !== null,
      );
      /* ⚠️ Reported even when SOME files were acceptable: a drop of four where one was a PDF has
       * to say so, or the missing chip is the only evidence and it is evidence of nothing. */
      const declined = sendable.length < chosen.length;

      if (sendable.length === 0) {
        setFailure('agent.files.notTabular');
        return null;
      }

      setAttaching(true);
      let refusal: string | null = null;
      let target: AgentConversation | null = null;
      try {
        target = conversation ?? (await create());

        for (const file of sendable) {
          try {
            const form = new FormData();
            form.set('file', file);
            const response = await session.request(
              `/files?target=agent_conversation&targetId=${target.id}`,
              { method: 'POST', body: form },
            );
            if (!response.ok) throw await refusalFrom(response);
            /* ⚠️ C47 · re-read rather than appended from the response. The upload answers with the
             * attachment it created, and pushing that into state would be a second place that
             * decides what a row looks like — including whether it is pending, which is a column
             * the response body has no reason to carry. One read, one shape. */
            await loadFiles(target.id);
          } catch (error) {
            const code = error instanceof AgentRefusal ? error.code : 'upstream_unavailable';
            /* ⚠️ **A blocked deployment ends the batch**, unlike a refused file: nothing else in
             * the list can succeed either, and the composer is about to be disabled with the
             * reason on it. */
            const block = blockedBy(code);
            if (block !== null) {
              setBlocked(block);
              return null;
            }
            refusal ??= code;
          }
        }
      } catch {
        /* ⚠️ The conversation could not be created, so there was never anywhere to put any of
         * them — one sentence for the batch rather than one per file. */
        refusal ??= 'upstream_unavailable';
      } finally {
        setAttaching(false);
      }

      /* ⚠️ The server's refusal first if there was one: it is about a file that actually got as
       * far as being read, which is the more specific thing to say. */
      if (refusal !== null) setFailure(errorKey(refusal));
      else if (declined) setFailure('agent.files.notTabular');
      return target;
    },
    [conversation, create, loadFiles, session],
  );

  const attach = useCallback(
    (chosen: readonly File[]) => {
      void upload(chosen);
    },
    [upload],
  );

  const send = useCallback(
    /** `given` · the thread the hand-off just attached files to — see `upload`. */
    (text: string, given?: AgentConversation | null) => {
      void (async () => {
        setFailure(null);

        /**
         * ⚠️ **The question is on screen before the request leaves.** Waiting for the server would
         * clear the composer and show nothing for as long as the round trip takes, which reads as a
         * swallowed message. The server persists it first for the same reason on its side — see
         * `beginTurn`.
         *
         * ⚠️ **`user:` prefixed ids, so an optimistic turn can never collide with a stored one.** A
         * stored id is a uuid; this is not, and the difference is deliberate — it is also how a test
         * can tell the two apart.
         */
        const localId = `user:${String(Date.now())}`;
        setTurns((current) => [
          ...current,
          {
            id: localId,
            role: 'user',
            text,
            thinking: null,
            steps: [],
            blocks: [],
            /* ⚠️ C48 · a person's own turn never offers an export — only a tool call does, and only
             * the model makes those. Empty rather than absent, so the shape is one shape. */
            offers: [],
            proposal: null,
            failed: false,
            compacted: false,
          },
        ]);
        /**
         * ┌───────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ C47 · THE PENDING FILES MOVE ONTO THIS QUESTION, LOCALLY, AT    │
         * │ THE SAME MOMENT THE SERVER DOES IT FOR REAL.                         │
         * └───────────────────────────────────────────────────────────────────────┘
         *
         * `beginTurn` binds them in the transaction that stores the message, so the truth is written
         * server-side — but the id it binds them to is the stored message's uuid, and the turn on
         * screen is still the optimistic `user:…` row. Re-reading the list here would therefore make
         * every chip vanish: bound to a message this client has never heard of.
         *
         * ⚠️ **So the local id is what they are bound to, and the list is NOT re-read after the
         * turn.** A navigation or a reload reads the truth, and the truth agrees — the same files,
         * against the stored id.
         *
         * ⚠️ **The one case where the two disagree** is a send that fails *before* the server writes
         * the message — a refusal, a dropped connection — which leaves the files pending on the
         * server and shown above a failed question here. Reopening the thread corrects it. The
         * alternative is holding the chips in a third place until the turn is known to have started,
         * to serve a case that resolves itself on the next read.
         */
        setFiles((current) =>
          current.map((file) => (file.messageId === null ? { ...file, messageId: localId } : file)),
        );
        setPhase('sending');

        let target = given ?? conversation;
        /* ⚠️ 29 Oct 2026 · hoisted so `finally` can tell whether the handle is still THIS turn's —
           the stream outlives `message_stop` (`consume`), and a newer question may own it by then. */
        let controller: AbortController | null = null;
        try {
          /* ⚠️ `??=` rather than an `if`, which lint is right to prefer — and it reads as what it is:
           * the thread exists, or this is the moment it starts. `create()` is C43's extraction, so
           * `attach` below can start one too. */
          target ??= await create();

          controller = new AbortController();
          abort.current = controller;

          await consume(
            agentTurn(session.request, target.id, text, controller.signal),
            `assistant:${localId}`,
            localId,
            target.id,
          );
        } catch (error) {
          setPhase('idle');
          /**
           * ⚠️⚠️ **A STOP IS NOT A FAILURE.** Somebody pressed the button; the turn ended because
           * they said so, and a red *"this question got no answer"* under a question they withdrew
           * would be the screen blaming itself for obeying.
           */
          if (error instanceof DOMException && error.name === 'AbortError') return; /* Stop. */
          if (error instanceof AgentRefusal) {
            const block = blockedBy(error.code);
            /* ⚠️ **Not marked when the FEATURE is off**, for the reason `Notice` gives: that is a
             * state of the deployment rather than something that went wrong with this question. */
            if (block !== null) {
              setBlocked(block);
              return;
            }
            markFailed(localId);
            setFailure(errorKey(error.code));
            return;
          }
          /* ⚠️ A network failure with no envelope behind it. `upstream_unavailable`'s sentence is
           * the true one — *"the model did not answer, your question is still in the
           * conversation"* — and it is, because the server persisted it before calling out. */
          markFailed(localId);
          setFailure('agent.error.upstream_unavailable');
        } finally {
          if (abort.current === controller) abort.current = null;
        }
      })();
    },
    /* ⚠️ C43 · `create` replaced the inline creation, and the navigation moved into it — so it is
     * the dependency now and `go` no longer is. Listed rather than suppressed: `create` is a
     * `useCallback` over `[go, session]`, so it is as stable as what it replaced. */
    [consume, conversation, create, markFailed, session],
  );

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ 16 Oct 2026 · A QUESTION ASKED ON KEZDŐLAP, ARRIVING HERE.                │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The home screen's composer navigates to `/agent` with its question and files left in
   * `agent-handoff.ts`. Taken ONCE — a ref, so React's double mount in development cannot send it
   * twice — and only on a fresh `/agent`: a thread's own address never consumes somebody else's
   * question. Files first, then the question, on the same thread.
   */
  const handedOver = useRef(false);
  useEffect(() => {
    if (handedOver.current || conversationId !== undefined) return;
    handedOver.current = true;
    const handoff = takeAgentHandoff();
    if (handoff === null) return;
    void (async () => {
      const target = handoff.files.length === 0 ? null : await upload(handoff.files);
      send(handoff.text, target);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival: `send` and
       `upload` change identity as the thread starts, and re-running would be a second question. */
  }, []);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE ONE GESTURE IN THIS SCREEN THAT CHANGES THE WORKSPACE, AND IT     │
   * │ SENDS NO VALUES. R-25.                                                    │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The server executes the input it stored when the proposal was made. If this function ever grows a
   * body, that is the thing to stop: it would make the accept endpoint a general-purpose write with
   * the agent's schema and no permission story of its own.
   *
   * ⚠️ **`working` first, so the buttons go before the round trip.** A second press is a second
   * request, and the server refuses it correctly — `proposal_already_used` — which is a refusal
   * nobody should have to read for pressing a button twice.
   */
  const acceptProposal = useCallback(
    (id: string) => {
      void (async () => {
        setFailure(null);
        markProposal(id, 'working');
        setPhase('sending');

        const controller = new AbortController();
        abort.current = controller;

        try {
          await consume(
            agentAccept(session.request, id, controller.signal),
            `accept:${id}`,
            null,
            conversation?.id ?? null,
          );
          /* ⚠️⚠️ **`approved`, NOT *"recorded"*.** The accept can succeed while the write is refused —
           * a closed month, a full day, somebody else's change — and the only thing that knows which
           * is the reply that just streamed in below the card. */
          markProposal(id, 'approved');
        } catch (error) {
          setPhase('idle');
          if (error instanceof DOMException && error.name === 'AbortError') {
            /* ⚠️ Stopped mid-answer. The WRITE already happened — this aborted the model's account of
             * it — so the card stays approved rather than reverting to two live buttons. */
            markProposal(id, 'approved');
            return;
          }
          if (error instanceof AgentRefusal) {
            const settled = REFUSED_STATE[error.code];
            markProposal(id, settled ?? 'pending');
            setFailure(errorKey(error.code));
            return;
          }
          /* ⚠️ **A network failure leaves the card PENDING, deliberately.** Nothing came back, so
           * whether the write happened is unknown; the server's single-use claim is what makes a
           * second press safe — it refuses if the first one landed. */
          markProposal(id, 'pending');
          setFailure('agent.error.upstream_unavailable');
        } finally {
          /* ⚠️ 29 Oct 2026 · only if still this stream's — `send`'s note. */
          if (abort.current === controller) abort.current = null;
        }
      })();
    },
    [consume, conversation, markProposal, session],
  );

  /**
   * Dismiss the proposed write.
   *
   * ⚠️ **No stream and no model call**, so no phase change: the card settles and the thread stays
   * exactly as it is. The model is told on its next question — `agent.service.ts` argues why paying
   * for a turn to acknowledge a dismissal is the wrong trade.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C46 · THE CARD, CORRECTED. IT ASKS FOR A NEW OFFER AND SWAPS IT IN  │
   * │ PLACE.                                                                   │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * R-25: the accept endpoint executes the input **it** stored. So this is not an accept with a
   * payload — the server merges these values into that stored input, re-runs the write tool's own
   * `run`, and answers with a **new proposal**. What lands in the thread is a fresh card with a new
   * id, under the same turn.
   *
   * ⚠️ **The replacement is matched by the OLD id**, because the new one did not exist when the
   * request went out. The card that comes back carries its own; `agent-proposal-card.tsx` keys its
   * edit state on it so the corrected card does not open holding the previous card's keystrokes.
   *
   * ⚠️ **No stream and no model call**, so no `phase`: a correction costs nothing and the composer
   * stays live. What the model eventually learns is the revised summary, quoted in the accept note.
   */
  const reviseProposal = useCallback(
    (id: string, values: Readonly<Record<string, string | null>>) => {
      void (async () => {
        setFailure(null);
        markProposal(id, 'working');
        try {
          const revised = await agentRevise(session.request, id, values);
          setTurns((current) =>
            current.map((turn) =>
              turn.proposal?.id === id ? { ...turn, proposal: revised } : turn,
            ),
          );
        } catch (error) {
          if (error instanceof AgentRefusal) {
            /* ⚠️ **The old card is gone on the server whatever happened here** — `claimForRevision`
             * moved it to `revised` before anything could fail — so leaving it `pending` would show
             * two live buttons over an offer that can no longer be accepted. `used` is the state the
             * contract has for exactly that: *already answered*. */
            markProposal(id, REFUSED_STATE[error.code] ?? 'used');
            setFailure(errorKey(error.code));
            return;
          }
          markProposal(id, 'used');
          setFailure('agent.error.upstream_unavailable');
        }
      })();
    },
    [markProposal, session],
  );

  const declineProposal = useCallback(
    (id: string) => {
      void (async () => {
        setFailure(null);
        markProposal(id, 'working');
        try {
          await agentDecline(session.request, id);
          markProposal(id, 'declined');
        } catch (error) {
          if (error instanceof AgentRefusal) {
            const settled = REFUSED_STATE[error.code];
            markProposal(id, settled ?? 'pending');
            setFailure(errorKey(error.code));
            return;
          }
          markProposal(id, 'pending');
          setFailure('agent.error.upstream_unavailable');
        }
      })();
    },
    [markProposal, session],
  );

  /* ── C27.4 · the history menu ────────────────────────────────────────────────────────────── */

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ REOPENING A THREAD IS A READ THROUGH THE ORDINARY PATH, AND THE       │
   * │ CARDS COME BACK RE-READ RATHER THAN REPLAYED. R-26.                       │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The server stores `{ kind, ids, readAt }` and never the rows, so what arrives here has been
   * through RLS, the check service and the field rules **on this request**. Nothing on this path
   * derives a row: the blocks are rendered exactly as they came, and a table short of what the
   * sentence above it describes says so in its own footer.
   *
   * ⚠️ **A turn in flight is abandoned first.** Switching threads mid-answer would otherwise leave
   * deltas arriving into a bubble that belongs to a conversation nobody is looking at — and, worse,
   * still costing money. `stop()` is a real cancellation.
   */
  const openConversation = useCallback(
    (id: string) => {
      void (async () => {
        abort.current?.abort();
        abort.current = null;
        setFailure(null);
        setPhase('idle');

        const response = await session.request(`/agent/conversations/${id}`);
        if (!response.ok) {
          const { code } = await refusalFrom(response);
          const block = blockedBy(code);
          if (block !== null) {
            setBlocked(block);
            return;
          }
          setFailure('agent.history.loadFailed');
          return;
        }

        const parsed = agentConversationDetailSchema.safeParse(
          (await response.json().catch(() => null)) as unknown,
        );
        /* ⚠️ A shape that does not parse leaves the thread as it was rather than half-loading one:
         * a transcript missing its middle is worse than one nobody opened. */
        if (!parsed.success) {
          setFailure('agent.history.loadFailed');
          return;
        }

        const detail = parsed.data;
        setConversation({
          id: detail.id,
          identifier: detail.identifier,
          title: detail.title,
          origin: detail.origin,
          createdAt: detail.createdAt,
          updatedAt: detail.updatedAt,
        });
        /**
         * ⚠️⚠️ **C47 · A SECOND REQUEST RATHER THAN A FIELD ON THE DETAIL, AND THE REASON IS THE
         * TRANSCRIPT'S SHAPE.** `agentMessageSchema` could carry a message's files, and then
         * `AgentService.history` would have to re-read them per turn and the pending ones — which
         * belong to no message — would need a home beside `messages` anyway. One list keyed on
         * `messageId` answers both halves, and it is the same list the panel and the strip read.
         *
         * ⚠️ **Not awaited.** The transcript is what somebody opened the thread to see; a chip
         * arriving a moment later is a chip appearing above a message that is already on screen,
         * while awaiting it would hold the whole thread behind a request about five rows.
         */
        void loadFiles(detail.id);
        setTurns(
          detail.messages.map((message, index) => ({
            id: message.id,
            role: message.role,
            text: message.text,
            thinking: message.thinking,
            /**
             * ⚠️⚠️ **C48 · RE-RESOLVED SERVER-SIDE, NOT REPLAYED FROM THE FRAME.** The transcript
             * stores the offer's ids and dates, never the names — so what arrives here has been read
             * back through the ordinary permission-checked services, and an offer whose project the
             * reader has since lost is simply not in this array. R-34, which is R-19's argument.
             */
            offers: message.offers,
            /**
             * ┌───────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ C49.1 · THE TOOL ROWS COME BACK, AND THEY COME BACK      │
             * │ BECAUSE THE SERVER WROTE THEM DOWN.                           │
             * └───────────────────────────────────────────────────────────────┘
             *
             * This was `steps: []` from C27.4 until now, with a comment saying the honest thing:
             * which tools ran was not stored, the frames were live, and *"inventing a row from a card
             * would be the client asserting a read it has no record of"*. That reasoning is why the
             * fix belonged on the server — `agent_messages.blocks` now carries a `tool_run` pointer
             * per call — and nothing here derives anything.
             *
             * ⚠️ **`state` is derived, because a stored run is finished.** `running` cannot survive a
             * reload; `done` versus `failed` is `errorCode === null`, which is why the wire shape has
             * no state to disagree with.
             *
             * ⚠️ **The id is positional and it is not a frame's id.** A live step is keyed by its
             * `toolCallId` so `tool_end` can find the row it settles; a stored one is never updated,
             * so the index within the turn is enough — and the `toolCallId` was deliberately not
             * stored, being an identifier for a conversation with the API rather than a fact about
             * this workspace.
             *
             * ⚠️ **Empty for every turn stored before C49.1**, so an old thread reads exactly as it
             * did rather than acquiring a history nobody recorded.
             */
            steps: message.tools.map((run, position) => ({
              id: `${message.id}-t${String(position)}`,
              name: run.name,
              state: run.errorCode === null ? ('done' as const) : ('failed' as const),
              count: run.count,
              errorCode: run.errorCode,
            })),
            blocks: message.blocks,
            proposal: message.proposal,
            /**
             * ┌───────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ A QUESTION WITH NO REPLY AFTER IT NEVER GOT ONE.         │
             * └───────────────────────────────────────────────────────────────┘
             *
             * ⚠️ **Derived, because the reason is not stored** — a turn that failed upstream leaves
             * the question (`beginTurn` writes it before calling the model, on purpose) and no
             * assistant row. So the shape *is* the record, and this reads it.
             *
             * ⚠️⚠️ **SOUND ONLY BECAUSE `history` PROJECTS ONE BUBBLE PER TURN.** The stored
             * transcript has `tool_result` and `confirmation` rows with the `user` role — the API's
             * vocabulary, not ours — and a rule like this over *those* would call every tool round a
             * failure. `AgentService.history` drops them, so in what arrives here a question is
             * followed by its answer or by nothing.
             */
            failed: message.role === 'user' && detail.messages[index + 1]?.role !== 'assistant',
            /* 29 Oct 2026 · stored on the turn, so a reopened thread shows the line where it was. */
            compacted: message.compacted,
          })),
        );
      })();
    },
    [loadFiles, session],
  );

  /**
   * Back to an empty thread, without touching the address.
   *
   * ⚠️ **A new thread is an empty screen, not a row.** The conversation is created lazily on the first
   * message — `POST /agent/conversations` on a click would write a row every time somebody changed
   * their mind, and since the menu is ordered by `updated_at` the list would fill with empty threads
   * ahead of the real ones.
   *
   * ⚠️ **It aborts whatever is in flight**, because leaving a thread means leaving its turn: a stream
   * still writing into `turns` after the screen has moved on would append an answer to the wrong
   * conversation. The server notices the closed socket and stops paying for the rest — see the
   * controller's loop.
   */
  const reset = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setConversation(null);
    setTurns([]);
    setFailure(null);
    setPhase('idle');
    /* ⚠️ C47 · and the files, which belong to the thread being left. Without this the chips of the
     * previous conversation would sit above the empty composer of a new one — and the pending strip
     * would offer to send somebody else's file with the first question. */
    setFiles([]);
  }, []);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE ADDRESS → THE THREAD. The only place a conversation is loaded.   │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **IT REACTS TO A CHANGE OF ADDRESS, NOT TO THE ADDRESS, AND THE FIRST VERSION LOST A LIVE
   * TURN BECAUSE OF THE DIFFERENCE.** Written as *"if the address names no thread, reset"* it fired
   * in the window between `send` putting the new conversation in state and the router committing the
   * `go` to its address — a render where a thread is open and `conversationId` is still
   * `undefined`. It read that as *"somebody asked for a new thread"* and cleared the question and the
   * answer streaming into it. Every case in `agent.test.tsx` that sends a message went red, which is
   * the only reason it was not shipped.
   *
   * ⚠️ **So `shown` is the address this effect has already acted on**, and the transient is skipped
   * because `undefined → undefined` is not a change. What survives is the behaviour that matters:
   * Back out of a thread, or click *Agent* in the sidebar, and the address stops naming a thread —
   * which is a real change, and resets.
   *
   * ⚠️ **The second guard is still needed for the first message of a NEW thread**: the address then
   * changes to a conversation this component created and already has, so there is nothing to load.
   *
   * ⚠️ **No `let live` flag**, matching every other route here: a `setState` after unmount is a
   * no-op, and a flag assigned only in a cleanup closure is a value TypeScript can prove never
   * changes — which the lint rule correctly calls an always-falsy check.
   */
  const shown = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (shown.current === conversationId) return;
    shown.current = conversationId;

    if (conversationId === undefined) {
      reset();
      return;
    }
    if (conversation?.id === conversationId) return;
    openConversation(conversationId);
  }, [conversation, conversationId, openConversation, reset]);

  /**
   * ⚠️ **Navigates rather than loads**, so `/agent` is a real address with a real screen and *new*
   * is undoable with Back. The effect above does the resetting.
   */
  const startNew = useCallback(() => {
    go('/agent');
  }, [go]);

  /**
   * ⚠️⚠️ **`confirm()`, AND IT IS THE ONE DESTRUCTIVE GESTURE ON THIS SCREEN.** The cascade takes the
   * transcript and any pending write with it (0042, 0045), which is not recoverable — and a menu row
   * one pixel from *"open this thread"* is exactly where a mis-click happens. The sentence says what
   * is lost.
   *
   * ⚠️ **The server is the gate regardless**: RLS narrows the delete to this member's own row, so the
   * worst a skipped confirmation could do is destroy something of your own.
   */
  const deleteConversation = useCallback(() => {
    const target = conversation;
    if (target === null) return;
    if (!window.confirm(t('agent.history.confirmDelete'))) return;

    void (async () => {
      abort.current?.abort();
      abort.current = null;

      const response = await session.request(`/agent/conversations/${target.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const { code } = await refusalFrom(response);
        setFailure(errorKey(code));
        return;
      }

      setConversations((current) => current.filter((entry) => entry.id !== target.id));
      /* ⚠️ **To `/agent`, not just cleared state.** The deleted thread's address would otherwise
       * still be in the bar — and a reload of it now answers `not_found`. */
      go('/agent');
    })();
  }, [conversation, go, session, t]);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ 29 Oct 2026 · THE OPEN THREAD, RENAMED FROM THE HISTORY MENU.             │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **Optimistic, per `state-rules.md` §7**: the menu shows the new name at once, the server's
   * stored row replaces it when it answers, and a refusal puts the old name back and says why on the
   * screen's one failure line. No undo toast: renaming back is the same three keystrokes.
   *
   * ⚠️ **The OPEN thread, like delete** — the row sits beside *Beszélgetés törlése* and acts on the
   * same thread. Its previous name is read from the list at the moment of the rename rather than
   * from `conversation`, which a summary frame may have changed since this callback was made.
   */
  const conversationsNow = useRef(conversations);
  conversationsNow.current = conversations;

  const renameConversation = useCallback(
    (title: string) => {
      const target = conversation;
      if (target === null) return;
      const previous =
        conversationsNow.current.find((entry) => entry.id === target.id)?.title ?? target.title;

      setFailure(null);
      retitle(target.id, title);

      void (async () => {
        const response = await session
          .request(`/agent/conversations/${target.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ title }),
          })
          .catch(() => null);

        if (response?.ok !== true) {
          retitle(target.id, previous);
          const code = response === null ? null : (await refusalFrom(response)).code;
          /* ⚠️ Only the two refusals with a sentence of their own; anything else is *"could not be
             renamed"* — `errorKey`'s default is about the model not answering, which nobody asked. */
          setFailure(
            code === 'conversation_not_renamable' || code === 'conversation_not_found'
              ? errorKey(code)
              : 'agent.history.renameFailed',
          );
          return;
        }

        const parsed = agentConversationSchema.safeParse(
          (await response.json().catch(() => null)) as unknown,
        );
        if (parsed.success) retitle(target.id, parsed.data.title);
      })();
    },
    [conversation, retitle, session],
  );

  /* ⚠️ C48 · one line to give this screen the product's export path — `use-export.tsx`'s own
   * promise. The Agent's offer resolves a filter; this is what runs it, once a person presses. */
  const exports = useExport();

  return (
    <>
      <AgentScreen
        /* ⚠️ `routes.tsx` gives the two addresses distinct markers so the reachability gate can tell
         * `/agent/<id>` from `/agent`; this is the half that renders whichever one is showing. */
        marker={conversationId === undefined ? 'agent' : 'agent-conversation'}
        turns={turns}
        phase={phase}
        blocked={blocked}
        failure={failure === null ? null : t(failure)}
        onSend={send}
        onAttach={attach}
        attaching={attaching}
        /**
         * ┌───────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ C47 · ONE LIST DOWN, THREE SURFACES UP.                         │
         * └───────────────────────────────────────────────────────────────────────┘
         *
         * The screen partitions it — `messageId === null` above the composer, everything else above the
         * message it names — rather than being handed three lists. A partition is a `filter` in one
         * place; three props are three chances for a chip to be in two of them.
         */
        files={files}
        library={library}
        libraryOpen={libraryOpen}
        libraryLoading={libraryLoading}
        onShowLibrary={showLibrary}
        onAttachExisting={attachExisting}
        onRemoveFile={removeFile}
        onStop={stop}
        /**
         * ⚠️ **A row on a data card opens the task at its own address**, which is what makes the
         * agent's answer a working task list rather than a picture of one. `/tasks/:key` exists
         * precisely so a task can be opened from anywhere — including from a key somebody pastes.
         */
        onOpenTask={(key) => {
          go(`/tasks/${key}`);
        }}
        /**
         * ┌───────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ C48 · THE OFFER'S FILTER GOES INTO THE PRODUCT'S OWN DIALOG.    │
         * └───────────────────────────────────────────────────────────────────────┘
         *
         * ⚠️ **The ids and the dates, not the names.** `projectName` and the rest exist so the control
         * could be READ before pressing; what the export takes is the ids, and passing a name would be
         * passing the one part of the offer the server has no use for.
         *
         * ⚠️ **`?? undefined` rather than `?? null`**, because `open`'s options are optional
         * properties: a `null` would be a value the hook then stores as a filter of null, which is not
         * the same as *"no filter"* on the wire.
         */
        onExport={(offer) => {
          exports.open(offer.domain, {
            from: offer.from ?? undefined,
            to: offer.to ?? undefined,
            projectId: offer.projectId ?? undefined,
            contractId: offer.contractId ?? undefined,
            memberId: offer.memberId ?? undefined,
          });
        }}
        onAcceptProposal={acceptProposal}
        onDeclineProposal={declineProposal}
        onReviseProposal={reviseProposal}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        openId={conversation?.id ?? null}
        /* ⚠️ **Navigates; the effect loads.** See the note on `conversationId` — one direction. */
        onOpen={(id) => {
          go(`/agent/${id}`);
        }}
        onNew={startNew}
        onDelete={deleteConversation}
        onRename={renameConversation}
      />
      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ C48 · THE PRODUCT'S OWN EXPORT DIALOG, OPENED PRE-FILLED. R-34. │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * ⚠️ **Here rather than in the screen, because `useExport` makes a REQUEST.** The screen is
       * presentational — it takes items and callbacks — and this hook holds a fetch, the
       * download-or-email branch and its own toasts. So the route owns it and the screen is handed
       * one more callback, exactly as it is for opening a task.
       *
       * ⚠️⚠️ **AND THE DIALOG IS WHERE THE PERSON CONFIRMS, WHICH IS THE WHOLE DESIGN.** The model
       * resolved a filter; the dialog is the surface that already knows how to run one — its range
       * controls, its refusals, its *"we have emailed it"* toast, its `EXPORT_MAX_ROWS` message. A
       * card in the thread with its own button would have been a second implementation of all four.
       */}
      {exports.dialog}
      {/* ⚠️ The hook's own toasts, merged into a `Toaster` this screen did not have: *"1 240 sor"*
          on a download, and the address on a queued one. Without this the export would run and say
          nothing, which for the emailed branch means nothing at all appears to happen. */}
      <Toaster
        toasts={[...exports.toasts]}
        label={t('agent.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={exports.dismiss}
      />
    </>
  );
}
